/**
 * フローティングパネル
 * パネルの位置や開閉状態を管理する
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ChevronDownIcon, XIcon } from 'lucide-react';

/**
 * パネルの位置タイプ
 */
export type PanelPosition = 'top' | 'right' | 'bottom' | 'left';

/**
 * フローティングパネルのプロパティ
 */
interface FloatingPanelProps {
  title?: string;
  position: PanelPosition;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  width?: string;
  height?: string;
  compact?: boolean;
  className?: string;
  showCloseButton?: boolean;
}

/**
 * チェブロンアイコンコンポーネント
 */
const ChevronIcon = ({ position, isOpen }: { position: PanelPosition, isOpen: boolean }) => {
  switch (position) {
    case 'right':
      return isOpen ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronLeftIcon className="h-4 w-4" />;
    case 'left':
      return isOpen ? <ChevronLeftIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />;
    case 'top':
      return isOpen ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />;
    case 'bottom':
      return isOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />;
    default:
      return <ChevronRightIcon className="h-4 w-4" />;
  }
};

/**
 * フローティングパネルコンポーネント
 */
const FloatingPanel: React.FC<FloatingPanelProps> = ({
  title,
  position,
  isOpen,
  onToggle,
  children,
  width = '320px',
  height = 'auto',
  compact = false,
  className = '',
  showCloseButton = true,
}) => {
  // ウィンドウサイズを監視して動的に調整
  const [windowDimensions, setWindowDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });
  
  // ウィンドウリサイズ時の処理
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // 位置ごとのスタイル設定
  const getPositionStyles = (): React.CSSProperties => {
    const isMobile = windowDimensions.width < 768;
    
    // サイドパネル幅をモバイルでは小さく
    const actualWidth = isMobile ? '280px' : width;
    // 高さがauto以外の場合は指定値を使用、そうでなければ画面に合わせる
    const sideHeight = height !== 'auto' 
      ? height 
      : `calc(${windowDimensions.height * 0.8}px)`;
    
    // 位置ごとのスタイル
    switch (position) {
      case 'top':
        return {
          top: isOpen ? '1rem' : '-100%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'auto',
          maxWidth: compact ? '600px' : '90%',
          transition: 'top 0.3s ease-in-out',
        };
      
      case 'right':
        return {
          top: '5rem',
          right: isOpen ? '1rem' : `-${actualWidth}`,
          width: actualWidth,
          height: sideHeight,
          maxHeight: `calc(${windowDimensions.height}px - 10rem)`,
          transition: 'right 0.3s ease-in-out',
        };
      
      case 'bottom':
        return {
          bottom: isOpen ? '1rem' : '-100%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'auto',
          maxWidth: compact ? '600px' : '90%',
          transition: 'bottom 0.3s ease-in-out',
        };
      
      case 'left':
        return {
          top: '5rem',
          left: isOpen ? '1rem' : `-${actualWidth}`,
          width: actualWidth,
          height: sideHeight,
          maxHeight: `calc(${windowDimensions.height}px - 10rem)`,
          transition: 'left 0.3s ease-in-out',
        };
      
      default:
        return {};
    }
  };
  
  // トグルボタンの位置
  const getToggleButtonStyles = (): React.CSSProperties => {
    switch (position) {
      case 'right':
        return {
          position: 'absolute',
          top: '50%',
          left: isOpen ? '0' : '0',
          transform: isOpen 
            ? 'translate(-50%, -50%)' 
            : 'translate(-50%, -50%) rotate(180deg)',
        };
      
      case 'left':
        return {
          position: 'absolute',
          top: '50%',
          right: isOpen ? '0' : '0',
          transform: isOpen 
            ? 'translate(50%, -50%)' 
            : 'translate(50%, -50%) rotate(180deg)',
        };
      
      case 'top':
        return {
          position: 'absolute',
          left: '50%',
          bottom: '0',
          transform: isOpen 
            ? 'translate(-50%, 50%)' 
            : 'translate(-50%, 50%) rotate(180deg)',
        };
      
      case 'bottom':
        return {
          position: 'absolute',
          left: '50%',
          top: '0',
          transform: isOpen 
            ? 'translate(-50%, -50%)' 
            : 'translate(-50%, -50%) rotate(180deg)',
        };
      
      default:
        return {};
    }
  };

  return (
    <div
      className="fixed z-10"
      style={getPositionStyles()}
    >
      <Card 
        className={cn(
          "shadow-lg bg-background/95 backdrop-blur-sm border border-border flex flex-col h-full",
          compact ? "p-0" : "",
          className
        )}
      >
        {title && (
          <div className="flex justify-between items-center px-4 py-2.5 border-b">
            <h3 className="text-sm font-medium">{title}</h3>
            {showCloseButton && (
              <Button variant="ghost" size="sm" onClick={onToggle} className="h-6 w-6 p-0">
                <XIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
        
        <CardContent className={cn(
          "flex-1 p-0 overflow-hidden",
          !title && "p-0"
        )}>
          {(isOpen || !title) && (
            <div className="h-full">
              {children}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* トグルボタン - パネルの外側に表示 */}
      <Button 
        variant="outline" 
        size="sm" 
        className="h-7 w-7 p-0 rounded-full bg-background shadow-md"
        onClick={onToggle}
        style={getToggleButtonStyles()}
        aria-label={isOpen ? "閉じる" : "開く"}
      >
        <ChevronIcon position={position} isOpen={isOpen} />
      </Button>
    </div>
  );
};

export default FloatingPanel;